import React, { useRef, useEffect, useState, useMemo, useCallback } from 'react';
import { Search, Filter, Upload, ChevronDown, ChevronUp, Send, Mic, Square, Bot, Users, CheckSquare, Trash2, X, Network, UserPlus } from 'lucide-react';
import { Attendee } from './live-session/SessionSetupView';
import { TranscriptEntry } from '../types';
import { GraphData, GraphNode } from '../services/sessionTasksService';

const formatRole = (role: string) => role.replace(/_/g, ' ');

const RESPONSE_MODES = [
  { value: 'open_ideas', label: 'Open Brainstorming' },
  { value: 'structured_brainstorm', label: 'Structured' },
  { value: 'solution_focused', label: 'Solution Focused' },
  { value: 'closing_down', label: 'Closing Down' },
  { value: 'next_steps', label: 'Next Steps' },
  { value: 'critique', label: 'Critique' },
  { value: 'silent', label: 'Silent' },
  { value: 'blank', label: 'Blank (No Response)' },
];

// Node type colors (subset of customerResearchGraphHelpers for the list view)
const NODE_TYPE_COLORS: Record<string, { color: string; bg: string; label: string }> = {
  Organisation: { color: '#3b82f6', bg: '#dbeafe', label: 'Organisation' },
  Person: { color: '#8b5cf6', bg: '#ede9fe', label: 'Person' },
  Product: { color: '#f59e0b', bg: '#fef3c7', label: 'Product' },
  Technology: { color: '#10b981', bg: '#d1fae5', label: 'Technology' },
  Industry: { color: '#6366f1', bg: '#e0e7ff', label: 'Industry' },
  Concept: { color: '#ec4899', bg: '#fce7f3', label: 'Concept' },
  Location: { color: '#14b8a6', bg: '#ccfbf1', label: 'Location' },
  Event: { color: '#f97316', bg: '#ffedd5', label: 'Event' },
  Session: { color: '#06b6d4', bg: '#cffafe', label: 'Session' },
  Contribution: { color: '#84cc16', bg: '#ecfccb', label: 'Contribution' },
  UserStory: { color: '#a855f7', bg: '#f3e8ff', label: 'User Story' },
  Persona: { color: '#e879f9', bg: '#fae8ff', label: 'Persona' },
  Decision: { color: '#ef4444', bg: '#fee2e2', label: 'Decision' },
  Task: { color: '#0ea5e9', bg: '#e0f2fe', label: 'Task' },
  Document: { color: '#64748b', bg: '#f1f5f9', label: 'Document' },
  DeviceImage: { color: '#78716c', bg: '#f5f5f4', label: 'Device Image' },
};

function getNodeTypeStyle(type: string) {
  return NODE_TYPE_COLORS[type] || { color: '#6b7280', bg: '#f3f4f6', label: type };
}

function getNodeDisplayName(node: GraphNode): string {
  if (node.name) return node.name;
  if (node.type === 'Contribution') {
    return node.avatarName
      ? `${node.avatarName}: ${(node.text || '').substring(0, 40)}${(node.text || '').length > 40 ? '...' : ''}`
      : (node.text || '').substring(0, 50) || node.id;
  }
  if (node.type === 'UserStory') {
    return node.story
      ? `${node.persona ? node.persona + ': ' : ''}${node.story.substring(0, 50)}${node.story.length > 50 ? '...' : ''}`
      : node.id;
  }
  return node.title || node.label || node.id;
}

interface AvatarInfo {
  role: string;
  name: string;
  emoji?: string;
  imageUrl?: string;
  primaryColor?: string;
  secondaryColor?: string;
}

interface TranscriptTabProps {
  transcript: TranscriptEntry[];
  avatars: AvatarInfo[];
  onImport: () => void;
  // Input bar props
  userInput: string;
  onUserInputChange: (value: string) => void;
  onSend: () => void;
  onSendToAvatars: (avatarRoles?: string[]) => void;
  isLoading: boolean;
  responseMode: string;
  onResponseModeChange: (mode: string) => void;
  isListeningContinuous: boolean;
  isSupported: boolean;
  onStartContinuous: () => void;
  onStopContinuous: () => void;
  interimTranscript: string;
  sessionMode: 'active' | 'readOnly';
  userAccessLevel: 'owner' | 'read' | 'write';
  avatarRequestLoading?: boolean;
  // Delete props
  onDeleteEntries?: (entries: { id: string; timestamp: string }[]) => Promise<void>;
  // Knowledge graph props
  graphData?: GraphData | null;
  onDeleteGraphNodes?: (nodeIds: string[]) => Promise<void>;
  // Attribution props
  sessionPeople?: Attendee[];
  onAttributeEntry?: (entryId: string, timestamp: string, attributedTo: Array<{ id: string; name: string; role: string; organization: string }>) => Promise<void>;
}

function AvatarCircle({ avatar, speaker }: { avatar?: AvatarInfo; speaker: 'human' | 'avatar' | 'whiteboard' }) {
  if (speaker === 'human') {
    return (
      <div className="size-10 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 border-2 border-blue-200 shadow-sm text-sm font-bold">
        H
      </div>
    );
  }

  if (speaker === 'whiteboard') {
    return (
      <div className="size-10 rounded-full bg-purple-100 flex items-center justify-center text-purple-600 border-2 border-purple-200 shadow-sm text-lg">
        🖼️
      </div>
    );
  }

  // Avatar speaker
  const borderColor = avatar?.primaryColor || '#818cf8'; // fallback to indigo-400
  const bgColor = avatar?.secondaryColor || '#e0e7ff'; // fallback to indigo-100

  if (avatar?.imageUrl) {
    return (
      <div className="relative">
        <div
          className="size-10 rounded-full bg-center bg-no-repeat bg-cover border-2 shadow-sm"
          style={{ backgroundImage: `url("${avatar.imageUrl}")`, borderColor }}
        />
        <div className="absolute -bottom-1 -right-1 bg-white rounded-full p-0.5 border border-slate-100 shadow-sm">
          <div className="size-3 bg-green-500 rounded-full border-2 border-white"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="relative">
      <div
        className="size-10 rounded-full flex items-center justify-center border-2 shadow-sm text-lg"
        style={{ backgroundColor: bgColor, borderColor, color: borderColor }}
      >
        {avatar?.emoji || '🤖'}
      </div>
      <div className="absolute -bottom-1 -right-1 bg-white rounded-full p-0.5 border border-slate-100 shadow-sm">
        <div className="size-3 bg-green-500 rounded-full border-2 border-white"></div>
      </div>
    </div>
  );
}

function SpeakerBadge({ speaker, avatar }: { speaker: 'human' | 'avatar' | 'whiteboard'; avatar?: AvatarInfo }) {
  if (speaker === 'human') {
    return (
      <span className="text-xs font-medium text-primary bg-primary/10 px-1.5 py-0.5 rounded">
        Facilitator
      </span>
    );
  }
  if (speaker === 'avatar') {
    const badgeStyle: React.CSSProperties = avatar?.primaryColor
      ? { color: avatar.primaryColor, backgroundColor: avatar.secondaryColor || `${avatar.primaryColor}1a`, borderColor: `${avatar.primaryColor}33` }
      : {};
    return (
      <span
        className={`text-xs font-medium px-1.5 py-0.5 rounded border ${avatar?.primaryColor ? '' : 'text-indigo-600 bg-indigo-50 border-indigo-100'}`}
        style={badgeStyle}
      >
        Virtual Avatar
      </span>
    );
  }
  if (speaker === 'whiteboard') {
    return (
      <span className="text-xs font-medium text-purple-600 bg-purple-50 px-1.5 py-0.5 rounded border border-purple-100">
        Whiteboard
      </span>
    );
  }
  return null;
}

function SystemDivider({ label }: { label: string }) {
  return (
    <div className="flex items-center gap-4 py-2">
      <div className="h-px bg-slate-200 flex-1"></div>
      <span className="text-xs font-bold text-slate-400 uppercase tracking-wide px-2 bg-slate-50 rounded-full border border-slate-100">
        {label}
      </span>
      <div className="h-px bg-slate-200 flex-1"></div>
    </div>
  );
}

const TIME_RANGES = [
  { label: '10m', minutes: 10 },
  { label: '30m', minutes: 30 },
  { label: '1h', minutes: 60 },
  { label: '2h', minutes: 120 },
  { label: '4h', minutes: 240 },
  { label: '24h', minutes: 1440 },
];

const COUNT_RANGES = [
  { label: 'Last 10', count: 10 },
  { label: 'Last 50', count: 50 },
  { label: 'Last 100', count: 100 },
];

function BulkSelectBar({
  selectedCount,
  totalVisible,
  onSelectAll,
  onDeselectAll,
  onDelete,
  deleteLoading,
  itemLabel,
  onSelectByTime,
  onSelectByCount,
}: {
  selectedCount: number;
  totalVisible: number;
  onSelectAll: () => void;
  onDeselectAll: () => void;
  onDelete: () => void;
  deleteLoading: boolean;
  itemLabel: string;
  onSelectByTime?: (minutes: number) => void;
  onSelectByCount?: (count: number) => void;
}) {
  return (
    <div className="flex flex-col gap-2 px-4 py-2 bg-blue-50 border border-blue-200 rounded-lg">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <span className="text-sm font-semibold text-blue-700">{selectedCount} selected</span>
          <button
            onClick={onSelectAll}
            className="text-xs text-blue-600 hover:text-blue-800 font-medium"
          >
            Select All ({totalVisible})
          </button>
          <button
            onClick={onDeselectAll}
            className="text-xs text-slate-500 hover:text-slate-700 font-medium"
          >
            Deselect All
          </button>
        </div>
        <button
          onClick={onDelete}
          disabled={selectedCount === 0 || deleteLoading}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-red-500 text-white text-xs font-bold hover:bg-red-600 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <Trash2 size={13} />
          {deleteLoading ? 'Deleting...' : `Delete ${selectedCount} ${itemLabel}`}
        </button>
      </div>
      {(onSelectByTime || onSelectByCount) && (
        <div className="flex items-center gap-2 flex-wrap">
          {onSelectByTime && (
            <>
              <span className="text-[11px] text-slate-500 font-medium">By time:</span>
              {TIME_RANGES.map(r => (
                <button
                  key={r.minutes}
                  onClick={() => onSelectByTime(r.minutes)}
                  className="px-2 py-0.5 text-[11px] font-semibold rounded bg-white border border-blue-200 text-blue-600 hover:bg-blue-100 hover:border-blue-300 transition-colors"
                >
                  {r.label}
                </button>
              ))}
            </>
          )}
          {onSelectByCount && (
            <>
              <span className={`text-[11px] text-slate-500 font-medium ${onSelectByTime ? 'ml-2 pl-2 border-l border-blue-200' : ''}`}>By count:</span>
              {COUNT_RANGES.map(r => (
                <button
                  key={r.count}
                  onClick={() => onSelectByCount(r.count)}
                  className="px-2 py-0.5 text-[11px] font-semibold rounded bg-white border border-blue-200 text-blue-600 hover:bg-blue-100 hover:border-blue-300 transition-colors"
                >
                  {r.label}
                </button>
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );
}

function ConfirmDialog({
  message,
  onConfirm,
  onCancel,
}: {
  message: string;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-[100]">
      <div className="bg-white rounded-xl shadow-xl p-6 max-w-sm mx-4">
        <p className="text-sm text-slate-700 mb-4">{message}</p>
        <div className="flex justify-end gap-3">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className="px-4 py-2 text-sm font-bold text-white bg-red-500 hover:bg-red-600 rounded-lg transition-colors"
          >
            Delete
          </button>
        </div>
      </div>
    </div>
  );
}

export function TranscriptTab({
  transcript,
  avatars,
  onImport,
  userInput,
  onUserInputChange,
  onSend,
  onSendToAvatars,
  isLoading,
  responseMode,
  onResponseModeChange,
  isListeningContinuous,
  isSupported,
  onStartContinuous,
  onStopContinuous,
  interimTranscript,
  sessionMode,
  userAccessLevel,
  avatarRequestLoading = false,
  onDeleteEntries,
  graphData,
  onDeleteGraphNodes,
  sessionPeople = [],
  onAttributeEntry,
}: TranscriptTabProps) {
  const feedRef = useRef<HTMLDivElement>(null);
  const [autoScroll, setAutoScroll] = useState(true);
  const [isExpanded, setIsExpanded] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [speakerFilter, setSpeakerFilter] = useState<string>('all');
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [showResponseModeDropdown, setShowResponseModeDropdown] = useState(false);
  const filterRef = useRef<HTMLDivElement>(null);
  const responseModeRef = useRef<HTMLDivElement>(null);
  const [showAvatarPicker, setShowAvatarPicker] = useState(false);
  const [selectedAvatarRoles, setSelectedAvatarRoles] = useState<Set<string>>(new Set());
  const avatarPickerRef = useRef<HTMLDivElement>(null);

  // Sub-tab state
  const [activeSubTab, setActiveSubTab] = useState<'transcript' | 'knowledge'>('transcript');

  // Select mode state (shared for both tabs)
  const [selectMode, setSelectMode] = useState(false);
  const [selectedTranscriptIds, setSelectedTranscriptIds] = useState<Set<string>>(new Set());
  const [selectedNodeIds, setSelectedNodeIds] = useState<Set<string>>(new Set());
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState<{ message: string; onConfirm: () => void } | null>(null);

  // Knowledge graph filter state
  const [graphTypeFilter, setGraphTypeFilter] = useState<string>('all');
  const [showGraphTypeDropdown, setShowGraphTypeDropdown] = useState(false);
  const graphTypeRef = useRef<HTMLDivElement>(null);

  // Attribution popover state
  const [attributionPopoverId, setAttributionPopoverId] = useState<string | null>(null);
  const [attributionLoading, setAttributionLoading] = useState<string | null>(null);
  const attributionPopoverRef = useRef<HTMLDivElement>(null);

  const handleToggleAttribution = useCallback(async (entry: TranscriptEntry, personId: string) => {
    if (!entry.id || !onAttributeEntry) return;
    const current = entry.attributedTo || [];
    const nextIds = current.includes(personId)
      ? current.filter(id => id !== personId)
      : [...current, personId];
    // Resolve full person objects from sessionPeople for the backend
    const nextPeople = nextIds.map(id => {
      const p = sessionPeople.find(sp => sp.id === id);
      return p ? { id: p.id, name: p.name, role: p.role, organization: p.organization } : { id, name: '', role: '', organization: '' };
    });
    setAttributionLoading(entry.id);
    try {
      await onAttributeEntry(entry.id, entry.timestamp, nextPeople);
    } finally {
      setAttributionLoading(null);
    }
  }, [onAttributeEntry, sessionPeople]);

  const isReadOnly = sessionMode === 'readOnly' || userAccessLevel === 'read';
  const currentModeLabel = RESPONSE_MODES.find(m => m.value === responseMode)?.label || 'Active';

  // Reset select mode when switching tabs
  useEffect(() => {
    setSelectMode(false);
    setSelectedTranscriptIds(new Set());
    setSelectedNodeIds(new Set());
  }, [activeSubTab]);

  // Auto-scroll on new entries
  useEffect(() => {
    if (autoScroll && feedRef.current && activeSubTab === 'transcript') {
      feedRef.current.scrollTop = feedRef.current.scrollHeight;
    }
  }, [transcript, autoScroll, activeSubTab]);

  // Close dropdowns on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (filterRef.current && !filterRef.current.contains(event.target as Node)) {
        setShowFilterDropdown(false);
      }
      if (responseModeRef.current && !responseModeRef.current.contains(event.target as Node)) {
        setShowResponseModeDropdown(false);
      }
      if (avatarPickerRef.current && !avatarPickerRef.current.contains(event.target as Node)) {
        setShowAvatarPicker(false);
      }
      if (graphTypeRef.current && !graphTypeRef.current.contains(event.target as Node)) {
        setShowGraphTypeDropdown(false);
      }
      if (attributionPopoverRef.current && !attributionPopoverRef.current.contains(event.target as Node)) {
        setAttributionPopoverId(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const getAvatarInfo = (avatarRole: string): AvatarInfo | undefined => {
    return avatars.find(a => a.role === avatarRole || a.name === avatarRole);
  };

  const getSpeakerName = (entry: TranscriptEntry): string => {
    if (entry.speaker === 'human') return 'Facilitator';
    if (entry.speaker === 'whiteboard') return entry.sourceName || 'Whiteboard';
    const avatar = getAvatarInfo(entry.avatarRole || '');
    return avatar?.name || formatRole(entry.avatarRole || 'Avatar');
  };

  // Filter transcript
  const filteredTranscript = transcript.filter(entry => {
    const matchesSearch = searchQuery === '' ||
      entry.text.toLowerCase().includes(searchQuery.toLowerCase()) ||
      getSpeakerName(entry).toLowerCase().includes(searchQuery.toLowerCase());
    const matchesSpeaker = speakerFilter === 'all' || entry.speaker === speakerFilter;
    return matchesSearch && matchesSpeaker;
  });

  // Filter graph nodes
  const graphNodes = graphData?.nodes || [];
  const graphNodeTypes = useMemo(() => {
    const types = new Set<string>();
    graphNodes.forEach(n => types.add(n.type));
    return Array.from(types).sort();
  }, [graphNodes]);

  const filteredGraphNodes = useMemo(() => {
    return graphNodes.filter(node => {
      const matchesSearch = searchQuery === '' ||
        getNodeDisplayName(node).toLowerCase().includes(searchQuery.toLowerCase());
      const matchesType = graphTypeFilter === 'all' || node.type === graphTypeFilter;
      return matchesSearch && matchesType;
    });
  }, [graphNodes, searchQuery, graphTypeFilter]);

  // Selectable transcript entries (only those with IDs)
  const selectableTranscript = filteredTranscript.filter(e => !!e.id);

  const handleToggleTranscriptSelect = (id: string) => {
    setSelectedTranscriptIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleToggleNodeSelect = (id: string) => {
    setSelectedNodeIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleSelectByTime = (minutes: number) => {
    if (selectableTranscript.length === 0) return;
    // Find the most recent entry timestamp and select entries within N minutes before it
    const latest = selectableTranscript.reduce((max, e) => {
      const t = new Date(e.timestamp).getTime();
      return t > max ? t : max;
    }, 0);
    const cutoff = new Date(latest - minutes * 60 * 1000);
    const ids = new Set<string>();
    for (const entry of selectableTranscript) {
      if (new Date(entry.timestamp) >= cutoff) {
        ids.add(entry.id!);
      }
    }
    setSelectedTranscriptIds(ids);
  };

  const handleSelectByCount = (count: number) => {
    if (selectableTranscript.length === 0) return;
    // Select the last N entries (most recent, by position in the array which is chronological)
    const lastN = selectableTranscript.slice(-count);
    setSelectedTranscriptIds(new Set(lastN.map(e => e.id!)));
  };

  const handleDeleteTranscript = () => {
    const entriesToDelete = transcript
      .filter(e => e.id && selectedTranscriptIds.has(e.id))
      .map(e => ({ id: e.id!, timestamp: e.timestamp }));

    setConfirmDialog({
      message: `Delete ${entriesToDelete.length} transcript ${entriesToDelete.length === 1 ? 'entry' : 'entries'}? This cannot be undone.`,
      onConfirm: async () => {
        setConfirmDialog(null);
        setDeleteLoading(true);
        try {
          await onDeleteEntries?.(entriesToDelete);
          setSelectedTranscriptIds(new Set());
          setSelectMode(false);
        } finally {
          setDeleteLoading(false);
        }
      },
    });
  };

  const handleDeleteGraphNodes = () => {
    const nodeIds = Array.from(selectedNodeIds);
    setConfirmDialog({
      message: `Delete ${nodeIds.length} knowledge graph ${nodeIds.length === 1 ? 'node' : 'nodes'} and their relationships? This cannot be undone.`,
      onConfirm: async () => {
        setConfirmDialog(null);
        setDeleteLoading(true);
        try {
          await onDeleteGraphNodes?.(nodeIds);
          setSelectedNodeIds(new Set());
          setSelectMode(false);
        } finally {
          setDeleteLoading(false);
        }
      },
    });
  };

  const getBubbleClasses = (speaker: string): string => {
    switch (speaker) {
      case 'human':
        return 'bg-chat-facilitator border border-blue-100';
      case 'avatar':
        return 'bg-white border-l-4 border-y border-r border-slate-200';
      case 'whiteboard':
        return 'bg-white border-l-4 border-l-purple-400 border-y border-r border-slate-200';
      default:
        return 'bg-white border border-slate-200';
    }
  };

  const getBubbleStyle = (speaker: string, avatar?: AvatarInfo): React.CSSProperties | undefined => {
    if (speaker === 'avatar') {
      return { borderLeftColor: avatar?.primaryColor || '#818cf8' };
    }
    return undefined;
  };

  const getNameStyle = (speaker: string, avatar?: AvatarInfo): React.CSSProperties | undefined => {
    if (speaker === 'avatar' && avatar?.primaryColor) {
      return { color: avatar.primaryColor };
    }
    return undefined;
  };

  const getNameClasses = (speaker: string): string => {
    switch (speaker) {
      case 'avatar':
        return '';
      default:
        return 'text-slate-900';
    }
  };

  const canDelete = !isReadOnly && (
    (activeSubTab === 'transcript' && !!onDeleteEntries) ||
    (activeSubTab === 'knowledge' && !!onDeleteGraphNodes)
  );

  return (
    <div className="flex flex-col flex-1 min-h-0 overflow-hidden relative">
      {/* Confirmation dialog */}
      {confirmDialog && (
        <ConfirmDialog
          message={confirmDialog.message}
          onConfirm={confirmDialog.onConfirm}
          onCancel={() => setConfirmDialog(null)}
        />
      )}

      {/* Toolbar */}
      <div className="border-b border-slate-100 bg-white/80 backdrop-blur-sm shrink-0 z-10">
        {/* Sub-tabs */}
        <div className="flex items-center gap-0 px-6 pt-2">
          <button
            onClick={() => setActiveSubTab('transcript')}
            className={`px-4 py-2 text-sm font-semibold border-b-2 transition-colors ${
              activeSubTab === 'transcript'
                ? 'border-primary text-primary'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >
            Transcript
          </button>
        </div>

        {/* Toolbar controls */}
        <div className="h-12 flex items-center justify-between px-6">
          <div className="flex items-center gap-4 flex-1">
            {/* Search */}
            <div className="relative flex items-center w-full max-w-md">
              <Search className="absolute left-3 text-slate-400" size={18} />
              <input
                className="w-full pl-10 pr-4 py-1.5 bg-slate-50 border border-slate-200 rounded-lg text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
                placeholder={activeSubTab === 'transcript' ? 'Search transcript...' : 'Search knowledge...'}
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>

            {/* Filter (context-dependent) */}
            {activeSubTab === 'transcript' ? (
              <div className="relative" ref={filterRef}>
                <button
                  onClick={() => setShowFilterDropdown(!showFilterDropdown)}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-200 bg-white text-slate-600 text-sm font-medium hover:bg-slate-50 transition-colors"
                >
                  <Filter size={16} />
                  {speakerFilter === 'all' ? 'All Speakers' : speakerFilter === 'human' ? 'Facilitator' : speakerFilter === 'avatar' ? 'Avatars' : 'Whiteboard'}
                  <ChevronDown size={14} />
                </button>
                {showFilterDropdown && (
                  <div className="absolute top-full left-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-lg min-w-[160px] z-50 py-1">
                    {[
                      { value: 'all', label: 'All Speakers' },
                      { value: 'human', label: 'Facilitator' },
                      { value: 'avatar', label: 'Avatars' },
                      { value: 'whiteboard', label: 'Whiteboard' },
                    ].map(opt => (
                      <button
                        key={opt.value}
                        onClick={() => { setSpeakerFilter(opt.value); setShowFilterDropdown(false); }}
                        className={`w-full text-left px-3 py-2 text-sm hover:bg-slate-50 transition-colors ${speakerFilter === opt.value ? 'text-primary font-semibold' : 'text-slate-700'}`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div className="relative" ref={graphTypeRef}>
                <button
                  onClick={() => setShowGraphTypeDropdown(!showGraphTypeDropdown)}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-200 bg-white text-slate-600 text-sm font-medium hover:bg-slate-50 transition-colors"
                >
                  <Filter size={16} />
                  {graphTypeFilter === 'all' ? 'All Types' : getNodeTypeStyle(graphTypeFilter).label}
                  <ChevronDown size={14} />
                </button>
                {showGraphTypeDropdown && (
                  <div className="absolute top-full left-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-lg min-w-[180px] z-50 py-1 max-h-[300px] overflow-y-auto">
                    <button
                      onClick={() => { setGraphTypeFilter('all'); setShowGraphTypeDropdown(false); }}
                      className={`w-full text-left px-3 py-2 text-sm hover:bg-slate-50 transition-colors ${graphTypeFilter === 'all' ? 'text-primary font-semibold' : 'text-slate-700'}`}
                    >
                      All Types
                    </button>
                    {graphNodeTypes.map(type => {
                      const style = getNodeTypeStyle(type);
                      return (
                        <button
                          key={type}
                          onClick={() => { setGraphTypeFilter(type); setShowGraphTypeDropdown(false); }}
                          className={`w-full text-left px-3 py-2 text-sm hover:bg-slate-50 transition-colors flex items-center gap-2 ${graphTypeFilter === type ? 'text-primary font-semibold' : 'text-slate-700'}`}
                        >
                          <span className="size-2.5 rounded-full shrink-0" style={{ backgroundColor: style.color }} />
                          {style.label}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="flex items-center gap-3">
            {/* Select mode toggle */}
            {canDelete && (
              <>
                <button
                  onClick={() => {
                    setSelectMode(!selectMode);
                    if (selectMode) {
                      setSelectedTranscriptIds(new Set());
                      setSelectedNodeIds(new Set());
                    }
                  }}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-sm font-medium transition-colors ${
                    selectMode
                      ? 'border-blue-300 bg-blue-50 text-blue-700'
                      : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50'
                  }`}
                  title={selectMode ? 'Exit select mode' : 'Select items to delete'}
                >
                  {selectMode ? <X size={16} /> : <CheckSquare size={16} />}
                  {selectMode ? 'Cancel' : 'Select'}
                </button>
                {!selectMode && <div className="w-px h-6 bg-slate-200"></div>}
              </>
            )}

            {activeSubTab === 'transcript' && !selectMode && (
              <>
                {/* Auto-scroll toggle */}
                <div className="flex items-center gap-1 px-2 py-1 bg-slate-100 rounded-lg">
                  <span className="text-xs font-medium text-slate-600 pl-2">Auto-scroll</span>
                  <button
                    onClick={() => setAutoScroll(!autoScroll)}
                    className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out ${autoScroll ? 'bg-primary' : 'bg-slate-300'}`}
                  >
                    <span className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${autoScroll ? 'translate-x-4' : 'translate-x-0'}`}></span>
                  </button>
                </div>
                <div className="w-px h-6 bg-slate-200 mx-1"></div>
                {/* Import */}
                <button
                  onClick={onImport}
                  className="text-slate-400 hover:text-primary transition-colors"
                  title="Import transcript"
                >
                  <Upload size={20} />
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Content area */}
      {activeSubTab === 'transcript' ? (
        <>
          {/* Bulk select bar for transcript */}
          {selectMode && activeSubTab === 'transcript' && (
            <div className="px-6 py-2 shrink-0">
              <BulkSelectBar
                selectedCount={selectedTranscriptIds.size}
                totalVisible={selectableTranscript.length}
                onSelectAll={() => setSelectedTranscriptIds(new Set(selectableTranscript.map(e => e.id!)))}
                onDeselectAll={() => setSelectedTranscriptIds(new Set())}
                onDelete={handleDeleteTranscript}
                deleteLoading={deleteLoading}
                itemLabel={selectedTranscriptIds.size === 1 ? 'entry' : 'entries'}
                onSelectByTime={handleSelectByTime}
                onSelectByCount={handleSelectByCount}
              />
            </div>
          )}

          {/* Transcript Feed */}
          <div ref={feedRef} className="flex-1 min-h-0 overflow-y-auto px-6 py-6 space-y-6 scroll-smooth pb-4">
            {filteredTranscript.length === 0 && transcript.length === 0 ? (
              /* Empty state */
              <div className="flex flex-col items-center justify-center py-20 text-center">
                <div className="size-16 bg-slate-100 rounded-full flex items-center justify-center mb-4">
                  <Search size={24} className="text-slate-400" />
                </div>
                <h3 className="text-lg font-semibold text-slate-700 mb-1">No conversation yet</h3>
                <p className="text-sm text-slate-500 max-w-sm">
                  Start by typing a message or importing a transcript to see the conversation feed here.
                </p>
              </div>
            ) : filteredTranscript.length === 0 ? (
              /* No results for filter/search */
              <div className="flex flex-col items-center justify-center py-20 text-center">
                <div className="size-16 bg-slate-100 rounded-full flex items-center justify-center mb-4">
                  <Filter size={24} className="text-slate-400" />
                </div>
                <h3 className="text-lg font-semibold text-slate-700 mb-1">No matching entries</h3>
                <p className="text-sm text-slate-500 max-w-sm">
                  Try adjusting your search or filter criteria.
                </p>
              </div>
            ) : (
              <>
                {/* Session start divider */}
                {filteredTranscript.length > 0 && (
                  <SystemDivider label={`Session Started • ${new Date(filteredTranscript[0].timestamp).toLocaleDateString([], { day: 'numeric', month: 'short' })} ${new Date(filteredTranscript[0].timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`} />
                )}

                {filteredTranscript.map((entry, idx) => {
                  const avatar = entry.speaker === 'avatar' ? getAvatarInfo(entry.avatarRole || '') : undefined;
                  const speakerName = getSpeakerName(entry);
                  const entryDate = new Date(entry.timestamp);
                  const timeStr = `${entryDate.toLocaleDateString([], { day: 'numeric', month: 'short' })} ${entryDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
                  const hasId = !!entry.id;
                  const isSelected = hasId && selectedTranscriptIds.has(entry.id!);

                  return (
                    <div
                      key={entry.id || idx}
                      className={`flex gap-4 group ${selectMode && isSelected ? 'bg-blue-50/50 -mx-2 px-2 rounded-lg' : ''}`}
                    >
                      {/* Checkbox in select mode */}
                      {selectMode && (
                        <div className="flex items-start pt-1 shrink-0">
                          {hasId ? (
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => handleToggleTranscriptSelect(entry.id!)}
                              className="size-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500/20 cursor-pointer"
                            />
                          ) : (
                            <div className="size-4" />
                          )}
                        </div>
                      )}
                      {/* Avatar */}
                      <div className="flex-shrink-0">
                        <AvatarCircle avatar={avatar} speaker={entry.speaker} />
                      </div>
                      {/* Content */}
                      <div className="flex-1">
                        {/* Header: name + badge + time */}
                        <div className="flex items-baseline gap-2 mb-1">
                          <span className={`text-sm font-bold ${getNameClasses(entry.speaker)}`} style={getNameStyle(entry.speaker, avatar)}>
                            {speakerName}
                          </span>
                          <SpeakerBadge speaker={entry.speaker} avatar={avatar} />
                          <span className="text-xs text-slate-400 ml-2">{timeStr}</span>
                        </div>
                        {/* Message bubble */}
                        <div className={`p-4 rounded-2xl rounded-tl-none text-slate-800 text-base leading-relaxed shadow-sm ${getBubbleClasses(entry.speaker)}`} style={getBubbleStyle(entry.speaker, avatar)}>
                          <div className="whitespace-pre-wrap">{entry.text}</div>
                        </div>
                        {/* Attribution row */}
                        {hasId && onAttributeEntry && sessionPeople.length > 0 && (
                          <div className="relative mt-1.5 flex flex-wrap items-center gap-1.5">
                            {/* Attributed chips */}
                            {(entry.attributedTo || []).map(personId => {
                              const person = sessionPeople.find(p => p.id === personId);
                              if (!person) return null;
                              return (
                                <span key={personId} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-violet-100 text-violet-700 text-xs font-medium">
                                  {person.name}
                                  <button
                                    onClick={() => handleToggleAttribution(entry, personId)}
                                    className="hover:text-violet-900 transition-colors"
                                    disabled={attributionLoading === entry.id}
                                  >
                                    <X size={10} />
                                  </button>
                                </span>
                              );
                            })}
                            {/* Attribute button */}
                            <button
                              onClick={() => setAttributionPopoverId(prev => prev === entry.id ? null : entry.id!)}
                              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-dashed border-slate-300 text-slate-400 text-xs hover:border-violet-400 hover:text-violet-600 transition-colors opacity-0 group-hover:opacity-100 focus:opacity-100"
                            >
                              <UserPlus size={10} />
                              {(entry.attributedTo?.length ?? 0) === 0 ? 'Attribute to...' : 'Edit'}
                            </button>
                            {/* Attribution popover */}
                            {attributionPopoverId === entry.id && (
                              <div
                                ref={attributionPopoverRef}
                                className="absolute left-0 top-7 z-50 w-56 rounded-xl bg-white border border-slate-200 shadow-lg py-1"
                              >
                                <div className="px-3 py-1.5 text-xs font-semibold text-slate-500 border-b border-slate-100">
                                  Attribute to
                                </div>
                                {sessionPeople.map(person => {
                                  const isChecked = (entry.attributedTo || []).includes(person.id);
                                  return (
                                    <label
                                      key={person.id}
                                      className="flex items-center gap-2.5 px-3 py-2 hover:bg-slate-50 cursor-pointer"
                                    >
                                      <input
                                        type="checkbox"
                                        checked={isChecked}
                                        disabled={attributionLoading === entry.id}
                                        onChange={() => handleToggleAttribution(entry, person.id)}
                                        className="size-3.5 rounded border-slate-300 text-violet-600 focus:ring-violet-500/20 cursor-pointer"
                                      />
                                      <div className="min-w-0">
                                        <div className="text-sm text-slate-800 truncate">{person.name}</div>
                                        {person.role && <div className="text-xs text-slate-400 truncate">{person.role}</div>}
                                      </div>
                                    </label>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </>
            )}
          </div>
        </>
      ) : (
        <>
          {/* Bulk select bar for knowledge */}
          {selectMode && activeSubTab === 'knowledge' && (
            <div className="px-6 py-2 shrink-0">
              <BulkSelectBar
                selectedCount={selectedNodeIds.size}
                totalVisible={filteredGraphNodes.length}
                onSelectAll={() => setSelectedNodeIds(new Set(filteredGraphNodes.map(n => n.id)))}
                onDeselectAll={() => setSelectedNodeIds(new Set())}
                onDelete={handleDeleteGraphNodes}
                deleteLoading={deleteLoading}
                itemLabel={selectedNodeIds.size === 1 ? 'node' : 'nodes'}
              />
            </div>
          )}

          {/* Knowledge Graph List */}
          <div className="flex-1 min-h-0 overflow-y-auto px-6 py-4">
            {graphNodes.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-20 text-center">
                <div className="size-16 bg-slate-100 rounded-full flex items-center justify-center mb-4">
                  <Network size={24} className="text-slate-400" />
                </div>
                <h3 className="text-lg font-semibold text-slate-700 mb-1">No knowledge graph data</h3>
                <p className="text-sm text-slate-500 max-w-sm">
                  Run research tasks or backfill sessions to populate the knowledge graph.
                </p>
              </div>
            ) : filteredGraphNodes.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-20 text-center">
                <div className="size-16 bg-slate-100 rounded-full flex items-center justify-center mb-4">
                  <Filter size={24} className="text-slate-400" />
                </div>
                <h3 className="text-lg font-semibold text-slate-700 mb-1">No matching entities</h3>
                <p className="text-sm text-slate-500 max-w-sm">
                  Try adjusting your search or type filter.
                </p>
              </div>
            ) : (
              <div className="space-y-1">
                {filteredGraphNodes.map(node => {
                  const typeStyle = getNodeTypeStyle(node.type);
                  const displayName = getNodeDisplayName(node);
                  const isSelected = selectedNodeIds.has(node.id);

                  return (
                    <div
                      key={node.id}
                      className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${
                        isSelected ? 'bg-blue-50' : 'hover:bg-slate-50'
                      } ${selectMode ? 'cursor-pointer' : ''}`}
                      onClick={selectMode ? () => handleToggleNodeSelect(node.id) : undefined}
                    >
                      {selectMode && (
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => handleToggleNodeSelect(node.id)}
                          onClick={(e) => e.stopPropagation()}
                          className="size-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500/20 cursor-pointer shrink-0"
                        />
                      )}
                      <span
                        className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-full shrink-0"
                        style={{ color: typeStyle.color, backgroundColor: typeStyle.bg }}
                      >
                        {typeStyle.label}
                      </span>
                      <span className="text-sm text-slate-800 truncate flex-1">{displayName}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </>
      )}

      {/* Bottom Input Bar (only on transcript tab) */}
      {!isReadOnly && activeSubTab === 'transcript' && !selectMode && (
        <div className={`shrink-0 bg-white border-t border-slate-200 p-6 z-20 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.05)]${isExpanded ? ' h-[50vh] flex flex-col' : ''}`}>
          <div className={`flex flex-col gap-3${isExpanded ? ' flex-1 min-h-0' : ''}`}>
            {/* Input Controls */}
            <div className="flex items-center justify-between mb-1">
              <div className="flex items-center gap-2">
                {/* Response Mode Dropdown */}
                <div className="relative" ref={responseModeRef}>
                  <button
                    onClick={() => setShowResponseModeDropdown(!showResponseModeDropdown)}
                    className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-slate-50 border border-slate-200 text-xs font-bold text-slate-600 hover:bg-slate-100 transition-colors"
                  >
                    <span className="text-indigo-500">🤖</span>
                    Response Mode: {currentModeLabel}
                    <ChevronDown size={14} />
                  </button>
                  {showResponseModeDropdown && (
                    <div className="absolute bottom-full left-0 mb-1 bg-white border border-slate-200 rounded-lg shadow-lg min-w-[200px] z-50 py-1">
                      {RESPONSE_MODES.map(mode => (
                        <button
                          key={mode.value}
                          onClick={() => { onResponseModeChange(mode.value); setShowResponseModeDropdown(false); }}
                          className={`w-full text-left px-3 py-2 text-sm hover:bg-slate-50 transition-colors ${responseMode === mode.value ? 'text-primary font-semibold' : 'text-slate-700'}`}
                        >
                          {mode.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="text-xs text-slate-400 font-medium">
                  Press <kbd className="px-1.5 py-0.5 rounded bg-slate-100 border border-slate-300 font-sans text-[10px]">Enter</kbd> to add to transcript
                </div>
                <button onClick={() => setIsExpanded(v => !v)} className='p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors' title={isExpanded ? 'Collapse input' : 'Expand input'}>{isExpanded ? <ChevronDown size={16} /> : <ChevronUp size={16} />}</button>
              </div>
            </div>
            {/* Interim transcript display when mic is active */}
            {isListeningContinuous && (
              <div className="flex items-center gap-2 px-3 py-2 bg-red-50 border border-red-100 rounded-lg">
                <span className="size-2 rounded-full bg-red-500 animate-pulse shrink-0" />
                <span className="text-sm text-slate-700 truncate">
                  {interimTranscript || <span className="text-slate-400 italic">Listening...</span>}
                </span>
              </div>
            )}
            {/* Input Field Area */}
            <div className={isExpanded ? 'flex gap-3 flex-1 min-h-0' : 'flex gap-3 items-end'}>
              <div className="relative flex-1">
                <textarea
                  value={userInput}
                  onChange={(e) => onUserInputChange(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      onSend();
                    }
                  }}
                  disabled={isListeningContinuous}
                  className={`w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 pr-12 text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary resize-none text-base shadow-inner disabled:opacity-50 ${isExpanded ? 'h-full' : 'h-[60px] min-h-[60px]'}`}
                  placeholder="Type your message to the group or ask the AI avatars..."
                />
              </div>
              {isExpanded ? (
                <div className='flex flex-col gap-2 shrink-0 justify-start'>
                {/* Mic Button */}
                {isSupported && (
                  <div className="flex flex-col gap-2 shrink-0">
                    <button
                      onClick={isListeningContinuous ? onStopContinuous : onStartContinuous}
                      className={`h-[60px] w-[60px] flex flex-col items-center justify-center rounded-xl transition-all group relative ${
                        isListeningContinuous
                          ? 'bg-red-100 hover:bg-red-200 text-red-600 border border-red-200'
                          : 'bg-red-50 hover:bg-red-100 text-red-500 border border-red-100'
                      }`}
                    >
                      {isListeningContinuous ? (
                        <>
                          <Square size={24} className="group-hover:scale-110 transition-transform" />
                          <span className="text-[9px] font-bold uppercase mt-0.5">Stop</span>
                        </>
                      ) : (
                        <>
                          <Mic size={24} className="group-hover:scale-110 transition-transform" />
                          <span className="text-[9px] font-bold uppercase mt-0.5">Live</span>
                        </>
                      )}
                      {isListeningContinuous && (
                        <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full animate-pulse"></span>
                      )}
                    </button>
                  </div>
                )}
                {/* Add to Transcript Button */}
                <button
                  onClick={onSend}
                  disabled={isLoading || !userInput.trim() || isListeningContinuous}
                  className="h-[60px] px-5 flex items-center justify-center gap-2 rounded-xl bg-slate-600 hover:bg-slate-700 text-white font-bold shadow-lg shadow-slate-600/20 transition-all active:scale-95 shrink-0 disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none text-sm"
                >
                  <Send size={18} />
                  <span>Add to Transcript</span>
                </button>
                {/* Send to Avatars Button with Dropup */}
                <div className="relative" ref={avatarPickerRef}>
                  <button
                    onClick={() => setShowAvatarPicker(!showAvatarPicker)}
                    disabled={avatarRequestLoading || isListeningContinuous}
                    className="h-[60px] px-5 flex items-center justify-center gap-2 rounded-xl bg-primary hover:bg-primary-dark text-white font-bold shadow-lg shadow-primary/30 transition-all active:scale-95 shrink-0 disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none text-sm"
                  >
                    <Bot size={18} />
                    <span>Send to Avatars</span>
                    <ChevronUp size={16} />
                  </button>
                  {showAvatarPicker && (
                    <div className="absolute bottom-full right-0 mb-2 bg-white border border-slate-200 rounded-xl shadow-xl min-w-[240px] z-50 py-2">
                      {/* All Avatars option */}
                      <button
                        onClick={() => {
                          onSendToAvatars();
                          setShowAvatarPicker(false);
                          setSelectedAvatarRoles(new Set());
                        }}
                        disabled={avatarRequestLoading}
                        className="w-full flex items-center gap-2 px-4 py-2.5 hover:bg-primary/5 text-sm font-semibold text-primary transition-colors"
                      >
                        <Users size={15} />
                        All Avatars
                      </button>
                      <div className="border-t border-slate-100 my-1" />
                      {/* Individual avatar checkboxes */}
                      <div className="max-h-[200px] overflow-y-auto">
                        {avatars.map(avatar => (
                          <label
                            key={avatar.role}
                            className="flex items-center gap-2.5 px-4 py-2 hover:bg-slate-50 cursor-pointer text-sm text-slate-700"
                          >
                            <input
                              type="checkbox"
                              checked={selectedAvatarRoles.has(avatar.role)}
                              onChange={(e) => {
                                const next = new Set(selectedAvatarRoles);
                                if (e.target.checked) next.add(avatar.role);
                                else next.delete(avatar.role);
                                setSelectedAvatarRoles(next);
                              }}
                              className="rounded border-slate-300 text-primary focus:ring-primary/20"
                            />
                            {avatar.name}
                          </label>
                        ))}
                      </div>
                      {/* Send Selected footer */}
                      {selectedAvatarRoles.size > 0 && (
                        <div className="border-t border-slate-100 mt-1 pt-2 px-3 flex gap-2">
                          <button
                            onClick={() => { setShowAvatarPicker(false); setSelectedAvatarRoles(new Set()); }}
                            className="text-xs py-1.5 px-3 text-slate-500 hover:text-slate-700 rounded-lg"
                          >
                            Cancel
                          </button>
                          <button
                            onClick={() => {
                              onSendToAvatars(Array.from(selectedAvatarRoles));
                              setSelectedAvatarRoles(new Set());
                              setShowAvatarPicker(false);
                            }}
                            className="flex-1 text-xs py-1.5 font-bold text-white bg-primary hover:bg-primary-dark rounded-lg transition-colors"
                          >
                            Send to Selected ({selectedAvatarRoles.size})
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
                </div>
              ) : (
                <>
              {/* Mic Button */}
              {isSupported && (
                <div className="flex flex-col gap-2 shrink-0">
                  <button
                    onClick={isListeningContinuous ? onStopContinuous : onStartContinuous}
                    className={`h-[60px] w-[60px] flex flex-col items-center justify-center rounded-xl transition-all group relative ${
                      isListeningContinuous
                        ? 'bg-red-100 hover:bg-red-200 text-red-600 border border-red-200'
                        : 'bg-red-50 hover:bg-red-100 text-red-500 border border-red-100'
                    }`}
                  >
                    {isListeningContinuous ? (
                      <>
                        <Square size={24} className="group-hover:scale-110 transition-transform" />
                        <span className="text-[9px] font-bold uppercase mt-0.5">Stop</span>
                      </>
                    ) : (
                      <>
                        <Mic size={24} className="group-hover:scale-110 transition-transform" />
                        <span className="text-[9px] font-bold uppercase mt-0.5">Live</span>
                      </>
                    )}
                    {isListeningContinuous && (
                      <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full animate-pulse"></span>
                    )}
                  </button>
                </div>
              )}
              {/* Add to Transcript Button */}
              <button
                onClick={onSend}
                disabled={isLoading || !userInput.trim() || isListeningContinuous}
                className="h-[60px] px-5 flex items-center justify-center gap-2 rounded-xl bg-slate-600 hover:bg-slate-700 text-white font-bold shadow-lg shadow-slate-600/20 transition-all active:scale-95 shrink-0 disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none text-sm"
              >
                <Send size={18} />
                <span>Add to Transcript</span>
              </button>
              {/* Send to Avatars Button with Dropup */}
              <div className="relative" ref={avatarPickerRef}>
                <button
                  onClick={() => setShowAvatarPicker(!showAvatarPicker)}
                  disabled={avatarRequestLoading || isListeningContinuous}
                  className="h-[60px] px-5 flex items-center justify-center gap-2 rounded-xl bg-primary hover:bg-primary-dark text-white font-bold shadow-lg shadow-primary/30 transition-all active:scale-95 shrink-0 disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none text-sm"
                >
                  <Bot size={18} />
                  <span>Send to Avatars</span>
                  <ChevronUp size={16} />
                </button>
                {showAvatarPicker && (
                  <div className="absolute bottom-full right-0 mb-2 bg-white border border-slate-200 rounded-xl shadow-xl min-w-[240px] z-50 py-2">
                    {/* All Avatars option */}
                    <button
                      onClick={() => {
                        onSendToAvatars();
                        setShowAvatarPicker(false);
                        setSelectedAvatarRoles(new Set());
                      }}
                      disabled={avatarRequestLoading}
                      className="w-full flex items-center gap-2 px-4 py-2.5 hover:bg-primary/5 text-sm font-semibold text-primary transition-colors"
                    >
                      <Users size={15} />
                      All Avatars
                    </button>
                    <div className="border-t border-slate-100 my-1" />
                    {/* Individual avatar checkboxes */}
                    <div className="max-h-[200px] overflow-y-auto">
                      {avatars.map(avatar => (
                        <label
                          key={avatar.role}
                          className="flex items-center gap-2.5 px-4 py-2 hover:bg-slate-50 cursor-pointer text-sm text-slate-700"
                        >
                          <input
                            type="checkbox"
                            checked={selectedAvatarRoles.has(avatar.role)}
                            onChange={(e) => {
                              const next = new Set(selectedAvatarRoles);
                              if (e.target.checked) next.add(avatar.role);
                              else next.delete(avatar.role);
                              setSelectedAvatarRoles(next);
                            }}
                            className="rounded border-slate-300 text-primary focus:ring-primary/20"
                          />
                          {avatar.name}
                        </label>
                      ))}
                    </div>
                    {/* Send Selected footer */}
                    {selectedAvatarRoles.size > 0 && (
                      <div className="border-t border-slate-100 mt-1 pt-2 px-3 flex gap-2">
                        <button
                          onClick={() => { setShowAvatarPicker(false); setSelectedAvatarRoles(new Set()); }}
                          className="text-xs py-1.5 px-3 text-slate-500 hover:text-slate-700 rounded-lg"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={() => {
                            onSendToAvatars(Array.from(selectedAvatarRoles));
                            setSelectedAvatarRoles(new Set());
                            setShowAvatarPicker(false);
                          }}
                          className="flex-1 text-xs py-1.5 font-bold text-white bg-primary hover:bg-primary-dark rounded-lg transition-colors"
                        >
                          Send to Selected ({selectedAvatarRoles.size})
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
